import { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthContext } from 'src/context/auth.context';
import useUpdateUser from 'src/api/organization/useUpdateUser';
import useUpdateUserPassword from 'src/api/organization/useUpdateUserPassword';
import { OrganizationMemberDTO } from 'src/api/organization/useOrganizationMembers';

interface EditUserModalProps {
  open: boolean;
  user: OrganizationMemberDTO | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`edit-user-tabpanel-${index}`}
      aria-labelledby={`edit-user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  user,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useContext(AuthContext);

  const [tabValue, setTabValue] = useState(0);
  const [detailsForm, setDetailsForm] = useState({
    name: '',
    email: '',
    isAdmin: false,
    isDeactivated: false,
  });
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const {
    mutateAsync: updateUser,
    isPending: isUpdatingUser,
    error: updateUserError,
  } = useUpdateUser(user?.userId || '');
  const {
    mutateAsync: updatePassword,
    isPending: isUpdatingPassword,
    error: updatePasswordError,
  } = useUpdateUserPassword(user?.userId || '');

  useEffect(() => {
    if (user) {
      setDetailsForm({
        name: user.name,
        email: user.email,
        isAdmin: user.isAdmin,
        isDeactivated: !!user.deactivatedAt,
      });
    }
  }, [user]);

  const handleDetailsInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setDetailsForm((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    };

  const handlePasswordInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordForm((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    };

  const validateDetailsForm = () => {
    const newErrors: Record<string, string> = {};

    if (!detailsForm.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!detailsForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(detailsForm.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors: Record<string, string> = {};

    if (!passwordForm.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (passwordForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateDetails = async () => {
    if (!validateDetailsForm() || !user) {
      return;
    }

    try {
      await updateUser(detailsForm);
      onSuccess();
      handleClose();
    } catch (err) {
      // Error is handled by the mutation hook
    }
  };

  const handleUpdatePassword = async () => {
    if (!validatePasswordForm() || !user) {
      return;
    }

    try {
      await updatePassword({ password: passwordForm.password });
      onSuccess();
      handleClose();
    } catch (err) {
      // Error is handled by the mutation hook
    }
  };

  const handleClose = () => {
    setTabValue(0);
    setPasswordForm({ password: '', confirmPassword: '' });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setErrors({});
    onClose();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setErrors({}); // Clear errors when switching tabs
  };

  if (!user) return null;

  const currentUserIsAdmin = currentUser?.isAdmin ?? false;
  const isPending = isUpdatingUser || isUpdatingPassword;
  const error = updateUserError || updatePasswordError;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User: {user.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Details" />
            <Tab label="Password" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error.message || 'Failed to update user'}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={detailsForm.name}
              onChange={handleDetailsInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              disabled={isPending}
            />

            <TextField
              label="Email"
              type="email"
              value={detailsForm.email}
              onChange={handleDetailsInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              disabled={isPending}
            />

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={detailsForm.isAdmin}
                    onChange={(e) =>
                      setDetailsForm((prev) => ({
                        ...prev,
                        isAdmin: e.target.checked,
                      }))
                    }
                    disabled={isPending || !currentUserIsAdmin}
                  />
                }
                label="Admin"
              />
              {!currentUserIsAdmin && (
                <Box
                  sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 1 }}
                >
                  Only admins can modify admin status
                </Box>
              )}
            </Box>

            {currentUserIsAdmin && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={detailsForm.isDeactivated}
                      onChange={(e) =>
                        setDetailsForm((prev) => ({
                          ...prev,
                          isDeactivated: e.target.checked,
                        }))
                      }
                      disabled={isPending}
                    />
                  }
                  label="Deactivated"
                />
                {user?.deactivatedAt && detailsForm.isDeactivated && (
                  <Box
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      mt: 1,
                    }}
                  >
                    Deactivated on:{' '}
                    {new Date(user.deactivatedAt).toLocaleString()}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={passwordForm.password}
              onChange={handlePasswordInputChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              disabled={isPending}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordForm.confirmPassword}
              onChange={handlePasswordInputChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              fullWidth
              disabled={isPending}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        {tabValue === 0 ? (
          <Button
            onClick={handleUpdateDetails}
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={20} /> : null}
          >
            {isPending ? 'Updating...' : 'Update Details'}
          </Button>
        ) : (
          <Button
            onClick={handleUpdatePassword}
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={20} /> : null}
          >
            {isPending ? 'Updating...' : 'Update Password'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
